import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TeamDetailsMachineInfoDto } from '../../../../core/models/teams/team-details-machine-info-dto';
import { BehaviorSubject } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatOptionModule } from '@angular/material/core';
import { MatSortModule } from '@angular/material/sort';
import { TeamListDto } from '../../../../core/models/teams/team-list-dto';
import { TeamListService } from '../../../../core/services/team-list/team-list.service';
import { MachineListDto } from '../../../../core/models/machines/machine-list-dto';
import { MachineDetailsTeamInfoDto } from '../../../../core/models/machines/machine-details-team-info-dto';
import { EditablePostClientStatus } from '../../../../core/enums/editable-post-client-status';
import { ProductionTechnicianArea, ProductionTechnicianAreaLabels } from '../../../../core/enums/production-technician-area';

@Component({
  selector: 'app-machine-add-team-dialog.component',
  imports: [MatIconModule, MatFormFieldModule, MatTooltipModule, MatInputModule, MatSelectModule, FormsModule, MatButtonModule, ReactiveFormsModule, MatCheckboxModule, MatOptionModule, MatProgressSpinnerModule, MatListModule, MatTableModule, MatSortModule, RouterModule, MatDialogModule, MatDialogContent],
  templateUrl: './machine-add-team-dialog.component.html',
  styleUrl: './machine-add-team-dialog.component.scss'
})
export class MachineAddTeamDialogComponent {
  constructor(public dialogRef: MatDialogRef<MachineAddTeamDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any, private teamListService: TeamListService,) { }

  showDeactivated: boolean = false;
  existingTeams: MachineDetailsTeamInfoDto[] = [];
  configuredTeams: MachineDetailsTeamInfoDto[] = [];
  allTeams: TeamListDto[] = [];

  allTeams$!: BehaviorSubject<TeamListDto[] | null>;
  teamsDataSource = new MatTableDataSource<TeamListDto>;
  displayColumns = ['select', 'teamId', 'productionTechnicianAreaText', 'baseInfo.name', 'productionInfo.statusInfo.statusText'];

  ngOnInit(): void {
    this.teamListService.loadTeams();
    this.existingTeams = this.data.machine.baseInfo.teams.filter((t: MachineDetailsTeamInfoDto) => t.clientStatus !== EditablePostClientStatus.Deleted);

    this.allTeams$ = this.teamListService.data$;
    this.allTeams$.subscribe(Teams => {
      if (Teams !== null) {
        this.allTeams = Teams;
        this.sortTeamsBasedOnAssignmentThenArea();
        this.teamsDataSource.data = this.allTeams.filter(t => t.statusInfo.statusText === "Aktivt");
        this.initializeFilterPredicate();
      }
    })
  }

  private initializeFilterPredicate() {
    this.teamsDataSource.filterPredicate = (team: TeamListDto, filter: string) => {
      const search = filter.trim().toLowerCase();
      return (
        team.teamId.toLowerCase().includes(search) ||
        team.productionTechnicianAreaText.toLowerCase().includes(search) ||
        team.baseInfo.name.toLowerCase().includes(search)
      );
    };
  }

  onCheckboxChange(team: TeamListDto, $event: any) {
    const productionTechnicianArea = this.getProductionTechnicianAreaFromLabel(team.productionTechnicianAreaText);

    if (!$event.checked && this.existingTeams.some(t => t.id === team.id)) {
      this.configuredTeams.push({
        id: team.id,
        teamId: team.teamId,
        name: team.baseInfo.name,
        clientStatus: EditablePostClientStatus.Deleted,
        active: team.statusInfo.statusText === "Aktivt",
        productionTechnicianArea: team.productionTechnicianAreaText
      });

      return;
    }

    var configuredTeamIndex = this.configuredTeams.findIndex(t => t.id === team.id);
    if (configuredTeamIndex === -1) {

      this.configuredTeams.push({
        id: team.id,
        teamId: team.teamId,
        name: team.baseInfo.name,
        productionTechnicianArea: team.productionTechnicianAreaText,
        clientStatus: EditablePostClientStatus.New,
        active: team.statusInfo.statusText === "Aktivt"
      });

      return;
    }

    this.configuredTeams.splice(configuredTeamIndex, 1);
  }

  private getProductionTechnicianAreaFromLabel(label: string): ProductionTechnicianArea {
    const areaEntry = Object.entries(ProductionTechnicianAreaLabels)
      .find(([key, value]) => value === label);
    return areaEntry ? Number(areaEntry[0]) : ProductionTechnicianArea.Unknown;
  }

  onChangeShowDeactivatedMachines(checked: boolean) {
    this.showDeactivated = checked;

    if (this.showDeactivated) {
      this.teamsDataSource.data = this.allTeams.filter(m => m.statusInfo.statusText);

      return;
    }

    this.teamsDataSource.data = this.allTeams.filter(t =>
      t.statusInfo.statusText === "Aktivt")
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.teamsDataSource.filter = filterValue.trim().toLowerCase();
  }

  private sortTeamsBasedOnAssignmentThenArea() {
    this.allTeams.sort((a, b) => {
      const aIsAssigned = this.teamIsAdded(a);
      const bIsAssigned = this.teamIsAdded(b);

      if (aIsAssigned !== bIsAssigned) {
        return bIsAssigned ? 1 : -1;
      }

      const areaComparison = a.productionTechnicianAreaText.localeCompare(b.productionTechnicianAreaText);
      if (areaComparison !== 0) {
        return areaComparison;
      }

      return a.teamId.localeCompare(b.teamId);
    });
  }

  teamIsAdded(team: TeamListDto): boolean {
    if (!this.existingTeams.some(m => m.id == team.id)) {
      return false;
    }

    return true;
  }

  onConfirm() {
    this.dialogRef.close(this.configuredTeams);
  }

  onCancel() {
    this.dialogRef.close(false);
  }
}
